import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Paper,
  Grid,
  Typography,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { DEFAULT_SEARCH_FILTERS } from '../../store/searchSlice';
import { fetchAccounts, selectAccounts } from '../../store/accountsSlice';

// A transaction number uniquely identifies a single record, so once one is
// entered every other filter (including the other transaction-number
// fields) is irrelevant to the result rather than silently ignored.
const TRANSACTION_NUMBER_FIELDS = ['cashSaleNumber', 'refundNumber', 'invoiceNumber', 'creditNoteNumber'];

// Controlled by the parent (backed by Redux) rather than owning its own
// filter state, so the entered values survive navigating away to a viewer
// page and back instead of resetting to blank.
const SearchFilters = ({ filters, onFiltersChange, onSearch, isSearching }) => {
  const dispatch = useDispatch();
  // All accounts (not just active ones) - searching should still find
  // historical transactions against an account that's since been stopped.
  const accounts = useSelector(selectAccounts);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (accounts.length === 0) {
      dispatch(fetchAccounts());
    }
  }, [accounts.length, dispatch]);

  const activeNumberField = useMemo(
    () => TRANSACTION_NUMBER_FIELDS.find(key => filters[key]),
    [filters]
  );
  const isDisabled = (field) => Boolean(activeNumberField) && activeNumberField !== field;

  const handleChange = (field) => (event) => {
    onFiltersChange({
      ...filters,
      [field]: event.target.value
    });
  };

  const handleDateChange = (field) => (newValue) => {
    onFiltersChange({
      ...filters,
      [field]: newValue ? format(newValue, 'yyyy-MM-dd') : ''
    });
  };

  const handleSearch = () => {
    onSearch(filters);
  };

  const handleClear = () => {
    onFiltersChange({ ...DEFAULT_SEARCH_FILTERS });
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Search Filters
          </Typography>
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Grid container spacing={2} onKeyPress={handleKeyPress}>
            {/* Customer Names */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Customer Name (Cash)"
                value={filters.customerNameCash}
                onChange={handleChange('customerNameCash')}
                placeholder="Search cash customers"
                disabled={isDisabled('customerNameCash')}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small" disabled={isDisabled('customerNameAccount')}>
                <InputLabel id="search-account-label">Customer Name (Account)</InputLabel>
                <Select
                  fullWidth
                  labelId="search-account-label"
                  value={filters.customerNameAccount}
                  onChange={handleChange('customerNameAccount')}
                  label="Customer Name (Account)"
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 400, minWidth: 320 } }
                  }}
                >
                  <MenuItem value="">
                    <em>All Accounts</em>
                  </MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.name}>
                      {acc.name} ({acc.sage})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Registration */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Registration"
                value={filters.registration}
                onChange={(e) => onFiltersChange({ ...filters, registration: e.target.value.toUpperCase() })}
                placeholder="Vehicle registration"
                disabled={isDisabled('registration')}
              />
            </Grid>

            {/* Part Number */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Part #"
                value={filters.partNumber}
                onChange={handleChange('partNumber')}
                placeholder="Part number"
                disabled={isDisabled('partNumber')}
              />
            </Grid>

            {/* Item Description */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Item"
                value={filters.itemDescription}
                onChange={handleChange('itemDescription')}
                placeholder="Item description"
                disabled={isDisabled('itemDescription')}
              />
            </Grid>

            {/* Order Number */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Order #"
                value={filters.orderNumber}
                onChange={handleChange('orderNumber')}
                placeholder="Order number"
                disabled={isDisabled('orderNumber')}
              />
            </Grid>

            {/* Transaction Numbers */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Cash Sale #"
                type="number"
                value={filters.cashSaleNumber}
                onChange={handleChange('cashSaleNumber')}
                placeholder="Cash sale ID"
                disabled={isDisabled('cashSaleNumber')}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Refund #"
                type="number"
                value={filters.refundNumber}
                onChange={handleChange('refundNumber')}
                placeholder="Refund ID"
                disabled={isDisabled('refundNumber')}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Invoice #"
                type="number"
                value={filters.invoiceNumber}
                onChange={handleChange('invoiceNumber')}
                placeholder="Invoice ID"
                disabled={isDisabled('invoiceNumber')}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Credit Note #"
                type="number"
                value={filters.creditNoteNumber}
                onChange={handleChange('creditNoteNumber')}
                placeholder="Credit note ID"
                disabled={isDisabled('creditNoteNumber')}
              />
            </Grid>

            {/* Date Range - blank until a date is actually chosen, no native
                browser "dd/mm/yyyy" ghost text */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <DatePicker
                label="Start Date"
                value={filters.startDate ? new Date(filters.startDate) : null}
                onChange={handleDateChange('startDate')}
                disabled={isDisabled('startDate')}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <DatePicker
                label="End Date"
                value={filters.endDate ? new Date(filters.endDate) : null}
                onChange={handleDateChange('endDate')}
                disabled={isDisabled('endDate')}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              disabled={isSearching}
            >
              Clear
            </Button>
          </Box>
        </Collapse>
      </Paper>
    </LocalizationProvider>
  );
};

export default SearchFilters;
